import { useState } from 'react';
import { useRecordContext } from 'react-admin';
import { Box, Button, IconButton, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { ArrowDropDown, ContentCopyOutlined } from '@mui/icons-material';
import startCase from 'lodash/startCase';
import useCopyToClipboard from '@/hooks/useCopyToClipboard';
import type { OneOffCheckReport } from '@/pages/Candidate/view/tables/OneOffChecksTable/types';
import {
  FieldLabelButton,
  FieldRow,
  FieldValue,
  FieldsHead,
  FieldsHeadAction,
  FieldsHeadCell,
  FieldsTable,
} from './styled';

const csvCell = (text: string) => `"${text.replace(/"/g, '""')}"`;

const sheetsCell = (text: string) => text.replace(/\t/g, ' ').replace(/\r?\n/g, ' ');

const toCsv = (rows: { label: string; value: string }[]) =>
  ['Custom Field,Value', ...rows.map((row) => `${csvCell(row.label)},${csvCell(row.value)}`)].join('\n');

const toSheets = (rows: { label: string; value: string }[]) =>
  ['Custom Field\tValue', ...rows.map((row) => `${sheetsCell(row.label)}\t${sheetsCell(row.value)}`)].join('\n');

const toJson = (rows: { label: string; value: string }[]) =>
  JSON.stringify(
    rows.map((row) => ({ customField: row.label, value: row.value })),
    null,
    2
  );

const OneOffCheckReportExpand = () => {
  const record = useRecordContext<OneOffCheckReport>();
  const { copyToClipboard } = useCopyToClipboard();
  const [copyAllAnchor, setCopyAllAnchor] = useState<HTMLElement | null>(null);
  const fields = record?.custom_fields?.filter((f) => (f.label?.trim() ?? '') !== '' || f.value != null) ?? [];
  const rows = fields.map((field) => ({ label: startCase(field.label || ''), value: field.value ?? '' }));

  const copyText = (text: string, message: string) => {
    void copyToClipboard({
      notifyProps: { onSuccess: { message } },
      textToCopy: text,
    });
  };

  if (!fields.length) {
    return (
      <Box sx={{ py: 2, px: 2 }}>
        <Typography variant="body2" color="text.secondary">
          No custom fields
        </Typography>
      </Box>
    );
  }

  const copyAll = (format: 'Sheets' | 'CSV' | 'JSON') => {
    setCopyAllAnchor(null);
    if (format === 'Sheets') {
      copyText(toSheets(rows), 'Custom fields copied for Sheets');
      return;
    }
    if (format === 'CSV') {
      copyText(toCsv(rows), 'Custom fields copied as CSV');
      return;
    }
    copyText(toJson(rows), 'Custom fields copied as JSON');
  };

  return (
    <FieldsTable>
      <FieldsHead>
        <FieldsHeadCell>Custom Field</FieldsHeadCell>
        <FieldsHeadCell>Value</FieldsHeadCell>
        <FieldsHeadAction>
          <Button
            aria-expanded={Boolean(copyAllAnchor)}
            aria-haspopup="true"
            endIcon={<ArrowDropDown sx={{ fontSize: 18 }} />}
            size="small"
            sx={{
              color: 'text.secondary',
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.2,
              minHeight: 0,
              minWidth: 0,
              px: 0.5,
              py: 0,
              textTransform: 'none',
            }}
            onClick={(event) => setCopyAllAnchor(event.currentTarget)}
          >
            Copy all
          </Button>
          <Menu anchorEl={copyAllAnchor} open={Boolean(copyAllAnchor)} onClose={() => setCopyAllAnchor(null)}>
            <MenuItem onClick={() => copyAll('Sheets')}>Sheets</MenuItem>
            <MenuItem onClick={() => copyAll('CSV')}>CSV</MenuItem>
            <MenuItem onClick={() => copyAll('JSON')}>JSON</MenuItem>
          </Menu>
        </FieldsHeadAction>
      </FieldsHead>
      {rows.map((row, index) => {
        const { label, value } = row;
        return (
          <FieldRow key={`${label || 'field'}-${index}`}>
            <FieldLabelButton
              aria-label={`Copy label ${label}`}
              type="button"
              onClick={() => copyText(label, `${label} label copied to clipboard`)}
            >
              {label}
            </FieldLabelButton>
            <FieldValue type="button" onClick={() => copyText(value, `${label} copied to clipboard`)}>
              {value || '—'}
            </FieldValue>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', pr: 0.5 }}>
              <Tooltip title="Copy value">
                <IconButton
                  aria-label={`Copy ${label} value`}
                  size="small"
                  sx={{ color: 'text.secondary' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    copyText(value, `${label} copied to clipboard`);
                  }}
                >
                  <ContentCopyOutlined fontSize="inherit" />
                </IconButton>
              </Tooltip>
            </Box>
          </FieldRow>
        );
      })}
    </FieldsTable>
  );
};

export default OneOffCheckReportExpand;
